import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';

// ─── Icons ────────────────────────────────────────────────────────────────────
function Ico({ children, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true">{children}</svg>
  );
}
const CheckCircle = ({ className }) => <Ico className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Ico>;
const PhoneIcon   = ({ className }) => <Ico className={className}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12 19.79 19.79 0 0 1 1.08 3.4 2 2 0 0 1 3.06 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16z"/></Ico>;
const KeyIcon     = ({ className }) => <Ico className={className}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></Ico>;
const SearchIcon  = ({ className }) => <Ico className={className}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ico>;
const ZapIcon     = ({ className }) => <Ico className={className}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></Ico>;
const ArrowRight  = ({ className }) => <Ico className={className}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></Ico>;
const ExternalLink = ({ className }) => <Ico className={className}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></Ico>;
const AlertCircle = ({ className }) => <Ico className={className}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></Ico>;
const Spinner     = ({ className = 'h-5 w-5' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
  </svg>
);

const STEPS = ['Welcome', 'Create Account', 'API Key', 'Pick Number', 'Done'];

// ─── Step indicator ────────────────────────────────────────────────────────────
function StepDots({ current }) {
  return (
    <div className="flex items-center gap-2 justify-center mb-8">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
              ${i < current ? 'bg-emerald-400 text-gray-900' :
                i === current ? 'bg-blue-500 text-white ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-900' :
                'bg-gray-700 text-gray-500'}`}>
              {i < current ? '✓' : i + 1}
            </div>
            <span className={`text-[10px] hidden sm:block transition-colors duration-300
              ${i === current ? 'text-blue-400' : i < current ? 'text-emerald-400' : 'text-gray-600'}`}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 h-px transition-colors duration-500 ${i < current ? 'bg-emerald-400' : 'bg-gray-700'}`}
              style={{ width: 24, flexGrow: 0, minWidth: 16 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────
export default function OnboardingWizard({ theme, onComplete }) {
  const [step, setStep] = useState(0);
  const [apiKey, setApiKey] = useState('');
  const [keyValid, setKeyValid] = useState(null);    // null | true | false
  const [keyLoading, setKeyLoading] = useState(false);
  const [keyError, setKeyError] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [numbers, setNumbers] = useState([]);
  const [numLoading, setNumLoading] = useState(false);
  const [numError, setNumError] = useState('');
  const [selectedNumber, setSelectedNumber] = useState('');
  const [provisioning, setProvisioning] = useState(false);
  const [provisionMsg, setProvisionMsg] = useState('');
  const [provisionError, setProvisionError] = useState('');
  const [doneNumber, setDoneNumber] = useState('');

  const isDark = theme === 'dark';
  const card   = isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
  const text   = isDark ? 'text-white' : 'text-gray-900';
  const sub    = isDark ? 'text-gray-400' : 'text-gray-500';
  const inp    = isDark
    ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500 focus:border-blue-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500';

  // Step 0 → Welcome
  const StepWelcome = () => (
    <div className="flex flex-col items-center text-center gap-6">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-emerald-400 flex items-center justify-center shadow-lg shadow-blue-500/30">
        <PhoneIcon className="h-9 w-9 text-white" />
      </div>
      <div>
        <h2 className={`text-2xl font-bold ${text} mb-2`}>Welcome to Portable PBX</h2>
        <p className={`${sub} text-sm leading-relaxed max-w-sm`}>
          Let's get your phone service set up. You'll need a free Telnyx account
          to make and receive calls — we'll walk you through every step.
        </p>
      </div>
      <div className={`w-full rounded-2xl border ${card} p-4 text-left space-y-3`}>
        {[
          ['🆓', 'Telnyx is free to sign up', 'You only pay for calls and numbers (pay-as-you-go)'],
          ['⚡', 'Takes about 3 minutes', 'Create account → get API key → pick a number → done'],
          ['🔐', 'Your key stays encrypted', 'We never store it in plain text'],
        ].map(([icon, title, desc]) => (
          <div key={title} className="flex gap-3 items-start">
            <span className="text-lg">{icon}</span>
            <div>
              <p className={`text-sm font-semibold ${text}`}>{title}</p>
              <p className={`text-xs ${sub}`}>{desc}</p>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => setStep(1)}
        className="w-full py-3 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-all">
        Get Started <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );

  // Step 1 → Create Telnyx account
  const [alreadyHasAccount, setAlreadyHasAccount] = useState(false);
  const StepCreateAccount = () => (
    <div className="flex flex-col gap-5">
      <div className="text-center">
        <h2 className={`text-xl font-bold ${text} mb-1`}>
          {alreadyHasAccount ? 'Great — use your existing account' : 'Create your Telnyx account'}
        </h2>
        <p className={`text-sm ${sub}`}>
          {alreadyHasAccount
            ? 'Skip to entering your API key below.'
            : 'Click the button to open Telnyx sign-up in a new tab.'}
        </p>
      </div>

      {!alreadyHasAccount && (
        <>
          <a href="https://telnyx.com/sign-up" target="_blank" rel="noopener noreferrer"
            className="w-full py-3 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center justify-center gap-2 transition-all">
            Open Telnyx Sign-Up <ExternalLink className="h-4 w-4" />
          </a>
          <div className={`rounded-xl border ${card} p-4 space-y-2 text-sm ${sub}`}>
            <p className="font-semibold text-xs uppercase tracking-wide text-gray-500">Once you're signed up:</p>
            {['Verify your email address', 'Log in to Mission Control Portal', 'Come back here and click Continue'].map(s => (
              <div key={s} className="flex gap-2 items-center">
                <div className="w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs">→</div>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <button onClick={() => setAlreadyHasAccount(v => !v)}
        className={`text-sm ${sub} underline underline-offset-2 text-center`}>
        {alreadyHasAccount ? '← I need to create an account' : 'I already have a Telnyx account →'}
      </button>

      <button onClick={() => setStep(2)}
        className="w-full py-3 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-all">
        Continue — Enter API Key <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );

  // Step 2 → API Key
  const validateKey = useCallback(async () => {
    if (!apiKey.trim()) return;
    setKeyLoading(true);
    setKeyError('');
    setKeyValid(null);
    try {
      const { data } = await api.post('/carrier/validate-key', { apiKey: apiKey.trim() });
      if (data.valid) {
        setKeyValid(true);
      } else {
        setKeyValid(false);
        setKeyError(data.error || 'Invalid API key.');
      }
    } catch (e) {
      setKeyValid(false);
      setKeyError(e.response?.data?.message || 'Could not validate key. Try again.');
    } finally {
      setKeyLoading(false);
    }
  }, [apiKey]);

  const StepApiKey = () => (
    <div className="flex flex-col gap-5">
      <div className="text-center">
        <h2 className={`text-xl font-bold ${text} mb-1`}>Enter your Telnyx API Key</h2>
        <p className={`text-sm ${sub}`}>Found in Mission Control → Auth → API Keys</p>
      </div>

      <div className={`rounded-xl border ${card} p-4 space-y-2 text-sm`}>
        <p className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>How to find your API key:</p>
        {[
          ['1', 'Log in at', 'portal.telnyx.com', 'https://portal.telnyx.com'],
          ['2', 'Go to', 'Auth → API Keys', null],
          ['3', 'Click', '"Create API Key"', null],
          ['4', 'Copy the key and paste it below', '', null],
        ].map(([num, pre, bold, href]) => (
          <div key={num} className="flex gap-2 items-start">
            <div className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex-shrink-0 flex items-center justify-center text-xs font-bold">{num}</div>
            <span className={sub}>{pre}{' '}
              {href
                ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">{bold}</a>
                : <strong className={text}>{bold}</strong>}
            </span>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <label className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>API Key</label>
        <div className="relative">
          <KeyIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${sub}`} />
          <input
            type="password"
            value={apiKey}
            onChange={e => { setApiKey(e.target.value); setKeyValid(null); setKeyError(''); }}
            onKeyDown={e => e.key === 'Enter' && validateKey()}
            placeholder="KEY01_..."
            className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm font-mono outline-none transition-all ${inp}
              ${keyValid === true ? 'border-emerald-500' : keyValid === false ? 'border-red-500' : ''}`}
          />
          {keyValid === true && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400" />}
          {keyValid === false && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-400" />}
        </div>
        {keyError && <p className="text-xs text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{keyError}</p>}
        {keyValid === true && <p className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle className="h-3 w-3" />API key validated successfully!</p>}
      </div>

      <div className="flex gap-3">
        <button onClick={() => setStep(1)}
          className={`flex-1 py-3 rounded-xl border ${card} ${sub} text-sm font-medium hover:opacity-80 transition-all`}>
          ← Back
        </button>
        {keyValid === true ? (
          <button onClick={() => setStep(3)}
            className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-all">
            Next — Pick Number <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={validateKey} disabled={!apiKey.trim() || keyLoading}
            className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold flex items-center justify-center gap-2 transition-all">
            {keyLoading ? <><Spinner className="h-4 w-4" /> Validating...</> : 'Validate Key'}
          </button>
        )}
      </div>
    </div>
  );

  // Step 3 → Pick number
  const searchNumbers = useCallback(async () => {
    if (!areaCode.trim()) return;
    setNumLoading(true);
    setNumError('');
    setNumbers([]);
    setSelectedNumber('');
    try {
      const { data } = await api.get(`/carrier/available-numbers?areaCode=${areaCode.trim()}&apiKey=${encodeURIComponent(apiKey)}`);
      if (!data || data.length === 0) {
        setNumError(`No numbers found for area code ${areaCode}. Try a different one.`);
      } else {
        setNumbers(data);
      }
    } catch (e) {
      setNumError(e.response?.data?.message || 'Search failed. Try again.');
    } finally {
      setNumLoading(false);
    }
  }, [areaCode, apiKey]);

  const provision = useCallback(async () => {
    if (!selectedNumber) return;
    setProvisioning(true);
    setProvisionError('');
    setProvisionMsg('Purchasing your number...');
    try {
      await new Promise(r => setTimeout(r, 800));
      setProvisionMsg('Creating SIP connection...');
      const { data } = await api.post('/carrier/purchase-and-connect', {
        apiKey: apiKey.trim(),
        phoneNumber: selectedNumber,
      });
      setProvisionMsg('Configuring softphone...');
      await new Promise(r => setTimeout(r, 600));
      setDoneNumber(data.phoneNumber || selectedNumber);
      setStep(4);
    } catch (e) {
      setProvisionError(e.response?.data?.message || 'Setup failed. Please try again.');
    } finally {
      setProvisioning(false);
      setProvisionMsg('');
    }
  }, [selectedNumber, apiKey]);

  const formatPhone = (num) => {
    const d = num.replace(/\D/g, '');
    if (d.length === 11) return `+1 (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    return num;
  };

  const StepPickNumber = () => (
    <div className="flex flex-col gap-5">
      <div className="text-center">
        <h2 className={`text-xl font-bold ${text} mb-1`}>Pick a phone number</h2>
        <p className={`text-sm ${sub}`}>Search by area code to find a local number</p>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${sub}`} />
          <input
            type="text"
            maxLength={3}
            value={areaCode}
            onChange={e => setAreaCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => e.key === 'Enter' && searchNumbers()}
            placeholder="Area code (e.g. 702)"
            className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-all ${inp}`}
          />
        </div>
        <button onClick={searchNumbers} disabled={areaCode.length !== 3 || numLoading}
          className="px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold text-sm transition-all flex items-center gap-2">
          {numLoading ? <Spinner className="h-4 w-4" /> : 'Search'}
        </button>
      </div>

      {numError && (
        <p className="text-xs text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{numError}</p>
      )}

      {numbers.length > 0 && (
        <div className={`rounded-xl border ${card} overflow-hidden`}>
          <div className={`px-4 py-2 border-b ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-100'}`}>
            <span className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>
              {numbers.length} numbers found · Area code {areaCode}
            </span>
          </div>
          <div className="divide-y divide-gray-700/50 max-h-52 overflow-y-auto">
            {numbers.map(n => (
              <label key={n.phoneNumber}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
                  ${selectedNumber === n.phoneNumber
                    ? isDark ? 'bg-blue-900/40' : 'bg-blue-50'
                    : isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'}`}>
                <input type="radio" name="number" value={n.phoneNumber}
                  checked={selectedNumber === n.phoneNumber}
                  onChange={() => setSelectedNumber(n.phoneNumber)}
                  className="accent-blue-500" />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold font-mono ${text}`}>{formatPhone(n.phoneNumber)}</p>
                  <p className={`text-xs ${sub}`}>{[n.locality, n.region].filter(Boolean).join(', ')}</p>
                </div>
                <span className="text-xs text-emerald-400 font-medium">${n.monthlyRate}/mo</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {provisionError && (
        <p className="text-xs text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{provisionError}</p>
      )}

      <div className="flex gap-3">
        <button onClick={() => setStep(2)}
          className={`flex-1 py-3 rounded-xl border ${card} ${sub} text-sm font-medium hover:opacity-80 transition-all`}>
          ← Back
        </button>
        <button onClick={provision} disabled={!selectedNumber || provisioning}
          className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-semibold flex items-center justify-center gap-2 transition-all">
          {provisioning
            ? <><Spinner className="h-4 w-4" />{provisionMsg || 'Setting up...'}</>
            : <><ZapIcon className="h-4 w-4" />Set Up My Phone</>}
        </button>
      </div>
    </div>
  );

  // Step 4 → Done
  const StepDone = () => (
    <div className="flex flex-col items-center text-center gap-6">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-blue-400 flex items-center justify-center shadow-lg shadow-emerald-500/30">
        <CheckCircle className="h-10 w-10 text-white" />
      </div>
      <div>
        <h2 className={`text-2xl font-bold ${text} mb-2`}>You're all set! 🎉</h2>
        <p className={`text-sm ${sub}`}>Your phone service is connected and ready to go.</p>
      </div>
      {doneNumber && (
        <div className={`w-full rounded-2xl border ${card} p-4 text-center`}>
          <p className={`text-xs uppercase tracking-wide ${sub} mb-1`}>Your phone number</p>
          <p className={`text-2xl font-bold font-mono ${text}`}>{formatPhone(doneNumber)}</p>
          <p className="text-xs text-emerald-400 mt-1">Ready to make and receive calls</p>
        </div>
      )}
      <div className={`w-full rounded-xl border ${card} p-4 text-left space-y-2 text-sm`}>
        {[
          ['✓', 'Softphone ready — make your first call'],
          ['✓', 'Inbound calls will ring in your browser'],
          ['✓', 'Upgrade to Pro for voicemail & SMS'],
          ['✓', 'Upgrade to Business for video rooms'],
        ].map(([icon, msg]) => (
          <div key={msg} className={`flex gap-2 items-center ${sub}`}>
            <span className="text-emerald-400 font-bold">{icon}</span>
            <span>{msg}</span>
          </div>
        ))}
      </div>
      <button onClick={onComplete}
        className="w-full py-3 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-all">
        Start Making Calls <PhoneIcon className="h-4 w-4" />
      </button>
    </div>
  );

  const STEP_COMPONENTS = [StepWelcome, StepCreateAccount, StepApiKey, StepPickNumber, StepDone];
  const CurrentStep = STEP_COMPONENTS[step];

  return (
    // Full-screen overlay
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className={`w-full max-w-md rounded-3xl shadow-2xl border overflow-hidden
          ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>

        {/* Header bar */}
        <div className={`px-6 pt-6 pb-2 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-emerald-400 flex items-center justify-center">
              <PhoneIcon className="h-3.5 w-3.5 text-white" />
            </div>
            <span className={`text-sm font-semibold ${text}`}>Phone Setup Wizard</span>
            <span className={`ml-auto text-xs ${sub}`}>Step {step + 1} of {STEPS.length}</span>
          </div>
          <StepDots current={step} />
        </div>

        {/* Step content */}
        <div className="px-6 py-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
          <AnimatePresence mode="wait">
            <motion.div key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}>
              <CurrentStep />
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
